/**
 * WlBillingIntegrationSection — bloco reutilizável de "Integração" para os
 * forms de Plano e Add-on no painel WL (Fase 3 / multi-gateway v1).
 *
 * Estrutura controlada (valor único: { billing_integration, billing_gateway_slug,
 * uaizy_master_service_id }). Pai mantém o estado, este componente apenas:
 *   1. Renderiza o select "Modo de integração" (Legado | Integração_V.1).
 *   2. Quando integracao_v1, mostra select de gateway (vem de wl_list_billing_gateways).
 *
 * OBS: A seção visual "Serviço de recorrência do tenant master" foi removida —
 * a periodicidade agora é definida pelos campos específicos (mensal/trimestral/
 * semestral/anual) no formulário. O campo `uaizy_master_service_id` permanece
 * na interface por compatibilidade com backend/hooks, apenas sem editor de UI.
 */
import { Plug } from "lucide-react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FormModalSection } from "@/components/shared/FormModalSection";
import { useWlBillingGateways } from "@/hooks/useWlBillingGateways";

export type WlBillingIntegrationMode = "legado" | "integracao_v1";

export interface WlBillingIntegrationValue {
  billing_integration: WlBillingIntegrationMode;
  billing_gateway_slug: string | null;
  uaizy_master_service_id: string | null;
}

interface Props {
  value: WlBillingIntegrationValue;
  onChange: (next: WlBillingIntegrationValue) => void;
  /** @deprecated Mantido por compatibilidade — não tem mais efeito visual. */
  showServicePreview?: boolean;
  /** Texto opcional acima dos campos (ex.: aviso de incompatibilidade). */
  helperSlot?: React.ReactNode;
}

export function WlBillingIntegrationSection({
  value,
  onChange,
  helperSlot,
}: Props) {
  const isV1 = value.billing_integration === "integracao_v1";

  const { data: gateways = [], isLoading: loadingGateways } = useWlBillingGateways(false);

  const update = (patch: Partial<WlBillingIntegrationValue>) =>
    onChange({ ...value, ...patch });

  return (
    <FormModalSection
      icon={Plug}
      title="Integração"
      description="Define como o plano/add-on é cobrado. Mantenha 'Legado' para o fluxo Stripe atual."
    >
      {helperSlot}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Modo de integração</Label>
          <Select
            value={value.billing_integration}
            onValueChange={(v) =>
              update({
                billing_integration: v as WlBillingIntegrationMode,
                // Ao voltar para legado, limpa gateway e serviço.
                billing_gateway_slug: v === "integracao_v1" ? value.billing_gateway_slug : null,
                uaizy_master_service_id: v === "integracao_v1" ? value.uaizy_master_service_id : null,
              })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="legado">Legado (Stripe)</SelectItem>
              <SelectItem value="integracao_v1">Integração V.1</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isV1 && (
          <div className="space-y-1">
            <Label className="text-xs">Gateway de cobrança</Label>
            <Select
              value={value.billing_gateway_slug || ""}
              onValueChange={(v) =>
                update({
                  billing_gateway_slug: v || null,
                  // Trocar gateway invalida o serviço selecionado.
                  uaizy_master_service_id:
                    v === value.billing_gateway_slug ? value.uaizy_master_service_id : null,
                })
              }
              disabled={loadingGateways || gateways.length === 0}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={loadingGateways ? "Carregando…" : "Selecione um gateway"}
                />
              </SelectTrigger>
              <SelectContent>
                {gateways.map((g) => (
                  <SelectItem key={g.slug} value={g.slug} disabled={!g.is_implemented}>
                    {g.name}
                    {!g.is_implemented ? " (em breve)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    </FormModalSection>
  );
}
